import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useTerritory } from "@/store/territory/TerritoryProvider";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformImmeuble, PlatformImmeubleInput } from "@/store/platform/types";

interface PlatformImmeubleFormProps {
  open: boolean;
  onClose: () => void;
  immeuble: PlatformImmeuble | null;
}

interface FormState {
  nom: string;
  description: string;
  photo: string;
  regionId: string;
  provinceId: string;
  communeId: string;
  secteurId: string;
  adresse: string;
  niveaux: string;
  infosComplementaires: string;
}

const EMPTY_STATE: FormState = {
  nom: "",
  description: "",
  photo: "",
  regionId: "",
  provinceId: "",
  communeId: "",
  secteurId: "",
  adresse: "",
  niveaux: "1",
  infosComplementaires: "",
};

/**
 * Édition d'un immeuble depuis le Super Admin.
 * La création reste réservée aux espaces Propriétaire / Agence ; le Super Admin
 * corrige les informations globales existantes.
 */
export default function PlatformImmeubleForm({ open, onClose, immeuble }: PlatformImmeubleFormProps) {
  const { updateImmeuble } = usePlatformData();
  const {
    regions,
    provinces,
    communes,
    secteurs,
    provincesOf,
    communesOf,
    secteurOf,
    regionName,
    provinceName,
    communeName,
    secteurName,
    loading,
    error,
    reload,
  } = useTerritory();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const regionOptions = useMemo(
    () => regions.map((region) => ({ value: region.id, label: region.nom })),
    [regions]
  );
  const provinceOptions = useMemo(
    () => provincesOf(form.regionId).map((province) => ({ value: province.id, label: province.nom })),
    [provincesOf, form.regionId]
  );
  const communeOptions = useMemo(
    () => communesOf(form.provinceId).map((commune) => ({ value: commune.id, label: commune.nom })),
    [communesOf, form.provinceId]
  );
  const secteurOptions = useMemo(
    () => secteurOf(form.communeId).map((secteur) => ({ value: secteur.id, label: secteur.nom })),
    [secteurOf, form.communeId]
  );

  useEffect(() => {
    if (!open || !immeuble) return;
    const regionId = immeuble.regionId || regions.find((item) => item.nom === immeuble.region)?.id || "";
    const provinceId =
      immeuble.provinceId ||
      provinces.find((item) => item.nom === immeuble.province && (!regionId || item.regionId === regionId))?.id ||
      "";
    const communeId =
      immeuble.communeId ||
      communes.find((item) => item.nom === immeuble.commune && (!provinceId || item.provinceId === provinceId))?.id ||
      "";
    const secteurId =
      immeuble.secteurId ||
      secteurs.find((item) => item.nom === immeuble.secteur && (!communeId || item.communeId === communeId))?.id ||
      "";
    setForm({
      nom: immeuble.nom,
      description: immeuble.description,
      photo: immeuble.photo,
      regionId,
      provinceId,
      communeId,
      secteurId,
      adresse: immeuble.adresse,
      niveaux: String(immeuble.niveaux),
      infosComplementaires: immeuble.infosComplementaires,
    });
    setErrors({});
  }, [open, immeuble]);

  if (!immeuble) return null;

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setForm((prev) => ({ ...prev, photo: URL.createObjectURL(file) }));
    }
  };

  const handleRegion = (value: string) =>
    setForm((prev) => ({ ...prev, regionId: value, provinceId: "", communeId: "", secteurId: "" }));
  const handleProvince = (value: string) =>
    setForm((prev) => ({ ...prev, provinceId: value, communeId: "", secteurId: "" }));
  const handleCommune = (value: string) => setForm((prev) => ({ ...prev, communeId: value, secteurId: "" }));

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.nom.trim()) nextErrors.nom = "Le nom de l'immeuble est obligatoire.";
    if (!form.regionId) nextErrors.regionId = "Sélectionnez une région.";
    if (!form.provinceId) nextErrors.provinceId = "Sélectionnez une province.";
    if (!form.communeId) nextErrors.communeId = "Sélectionnez une commune ou une ville.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: PlatformImmeubleInput = {
      scope: immeuble.scope,
      proprietaireId: immeuble.proprietaireId,
      nom: form.nom.trim(),
      description: form.description.trim(),
      photo: form.photo,
      regionId: form.regionId,
      provinceId: form.provinceId,
      communeId: form.communeId,
      secteurId: form.secteurId,
      region: regionName(form.regionId),
      province: provinceName(form.provinceId),
      commune: communeName(form.communeId),
      secteur: secteurName(form.secteurId),
      adresse: form.adresse.trim(),
      niveaux: Math.max(1, Number(form.niveaux) || 1),
      infosComplementaires: form.infosComplementaires.trim(),
    };
    updateImmeuble(immeuble.id, input);
    onClose();
  };

  const provincePlaceholder = !form.regionId
    ? "Choisissez d'abord une région"
    : provinceOptions.length > 0
      ? "Sélectionnez une province"
      : "Aucune province disponible";
  const communePlaceholder = !form.provinceId
    ? "Choisissez d'abord une province"
    : communeOptions.length > 0
      ? "Sélectionnez une commune"
      : "Aucune commune disponible";
  const secteurPlaceholder = !form.communeId
    ? "Choisissez d'abord une commune"
    : secteurOptions.length > 0
      ? "Sélectionnez un secteur / quartier"
      : "Aucun secteur disponible";

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Modifier l'immeuble"
      description="Corrigez les informations de cet immeuble. Le rattachement (Propriétaire / Agence) est conservé."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-save-line text-base" aria-hidden="true" />
            Enregistrer
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2 rounded-md bg-background-100 px-3.5 py-2.5 text-xs text-foreground-700">
          <span className="flex items-center gap-1.5">
            <i className="ri-error-warning-line text-base text-accent-600" aria-hidden="true" />
            Impossible de charger les territoires. {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="inline-flex items-center gap-1 whitespace-nowrap font-label font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
          >
            <i className="ri-refresh-line text-sm" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}
      {loading && !error && <p className="mb-3.5 text-xs text-foreground-500">Chargement des territoires…</p>}

      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
        <Field label="Nom de l'immeuble" htmlFor="sa-imm-nom" required error={errors.nom} className="sm:col-span-2">
          <Input
            id="sa-imm-nom"
            value={form.nom}
            onChange={(event) => update("nom", event.target.value)}
            invalid={Boolean(errors.nom)}
            maxLength={120}
          />
        </Field>

        <Field
          label="Photo"
          htmlFor="sa-imm-photo"
          hint="Aperçu local (frontend). L'envoi réel viendra avec le stockage."
          className="sm:col-span-2"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="h-24 w-full overflow-hidden rounded-md border border-background-200 bg-background-100 sm:w-40">
              {form.photo ? (
                <img src={form.photo} alt="Aperçu de l'immeuble" className="h-24 w-full object-cover object-top" />
              ) : (
                <div className="flex h-24 w-full items-center justify-center text-foreground-300">
                  <i className="ri-image-add-line text-3xl" aria-hidden="true" />
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-upload-2-line text-base" aria-hidden="true" />
                Choisir une image
              </button>
              {form.photo && (
                <button
                  type="button"
                  onClick={() => update("photo", "")}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-base" aria-hidden="true" />
                  Retirer
                </button>
              )}
            </div>
            <input
              ref={fileRef}
              id="sa-imm-photo"
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
            />
          </div>
        </Field>

        <Field label="Région" htmlFor="sa-imm-region" required error={errors.regionId}>
          <Select
            id="sa-imm-region"
            value={form.regionId}
            onChange={(event) => handleRegion(event.target.value)}
            options={regionOptions}
            placeholder="Sélectionnez une région"
            disabled={loading || regionOptions.length === 0}
            invalid={Boolean(errors.regionId)}
          />
        </Field>
        <Field label="Province" htmlFor="sa-imm-province" required error={errors.provinceId}>
          <Select
            id="sa-imm-province"
            value={form.provinceId}
            onChange={(event) => handleProvince(event.target.value)}
            options={provinceOptions}
            placeholder={provincePlaceholder}
            disabled={!form.regionId || provinceOptions.length === 0}
            invalid={Boolean(errors.provinceId)}
          />
        </Field>
        <Field label="Commune / Ville" htmlFor="sa-imm-commune" required error={errors.communeId}>
          <Select
            id="sa-imm-commune"
            value={form.communeId}
            onChange={(event) => handleCommune(event.target.value)}
            options={communeOptions}
            placeholder={communePlaceholder}
            disabled={!form.provinceId || communeOptions.length === 0}
            invalid={Boolean(errors.communeId)}
          />
        </Field>
        <Field label="Secteur / Quartier" htmlFor="sa-imm-secteur">
          <Select
            id="sa-imm-secteur"
            value={form.secteurId}
            onChange={(event) => update("secteurId", event.target.value)}
            options={secteurOptions}
            placeholder={secteurPlaceholder}
            disabled={!form.communeId || secteurOptions.length === 0}
          />
        </Field>

        <Field label="Adresse" htmlFor="sa-imm-adresse" className="sm:col-span-2">
          <Input
            id="sa-imm-adresse"
            value={form.adresse}
            onChange={(event) => update("adresse", event.target.value)}
            maxLength={160}
          />
        </Field>

        <Field label="Nombre de niveaux" htmlFor="sa-imm-niveaux">
          <Input
            id="sa-imm-niveaux"
            type="number"
            min={1}
            max={50}
            value={form.niveaux}
            onChange={(event) => update("niveaux", event.target.value)}
          />
        </Field>

        <Field label="Informations complémentaires" htmlFor="sa-imm-infos" className="sm:col-span-2">
          <Textarea
            id="sa-imm-infos"
            value={form.infosComplementaires}
            onChange={(event) => update("infosComplementaires", event.target.value)}
            className="min-h-[88px]"
            maxLength={600}
          />
        </Field>

        <Field label="Description" htmlFor="sa-imm-description" className="sm:col-span-2">
          <Textarea
            id="sa-imm-description"
            value={form.description}
            onChange={(event) => update("description", event.target.value)}
            className="min-h-[88px]"
            maxLength={600}
          />
        </Field>
      </div>
    </Modal>
  );
}